// src/components/InlineField.tsx
//
// Shared inline-edit field. Click to enter edit mode, save-on-blur, Enter
// commits, Escape reverts, optimistic update owned by parent. The parent's
// onSave promise contract drives state transitions: resolve → exit edit mode,
// reject → revert + toast + exit edit mode (user can retry by clicking again).
// Validation failures (empty-on-required, custom validate) keep the user in
// edit mode with an inline error message below the field; the save never
// fires until validation passes.
//
// Variants:
//   text / textarea / number / date / time — scalar values, save-on-blur
//   select — options[] passed by parent; save-on-change (no blur needed,
//            the change IS the commit). Compound writes (e.g. contact-select
//            writing contact_id + contact_name + contact_phone) work by the
//            parent's onSave handler unpacking the full Option object and
//            issuing the multi-column UPDATE.
//
// API discipline: 9 props max. Special-purpose selects (contact / agent /
// location / vendor / service_line) collapse into the generic `select`
// variant with parent-supplied options — keeps the API surface small.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

export type InlineFieldVariant = 'text' | 'textarea' | 'number' | 'select' | 'date' | 'time'

export type InlineFieldOption = { value: string; label: string; [key: string]: any }

type Props = {
  variant: InlineFieldVariant
  value: string | number | null | undefined
  /** For select variants where `value` is an FK id, this is the human-readable label shown in read mode. */
  displayValue?: string
  /** Called with the new value (scalar) for non-select variants, or the full Option object for `select`. Must return a Promise. */
  onSave: (next: any) => Promise<void>
  /** Required for `select`. */
  options?: InlineFieldOption[]
  placeholder?: string
  isLocked?: boolean
  required?: boolean
  /** Optional custom validator. Return error message string to reject, or null to accept. */
  validate?: (value: any) => string | null
  /** Escape hatch for font-size/weight overrides applied to both read and edit renders. Used for large fields like the page title h1. */
  valueStyle?: React.CSSProperties
}

export default function InlineField({
  variant, value, displayValue, onSave, options,
  placeholder = '—', isLocked = false, required = false, validate, valueStyle,
}: Props) {
  const [editing, setEditing] = useState(false)
  const [local, setLocal] = useState<string>(value == null ? '' : String(value))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null)
  const initialRef = useRef<string>(local)

  // Sync from parent on external value changes — but only when not actively
  // editing, so a live blur-save doesn't fight an in-flight query refetch.
  useEffect(() => {
    if (editing) return
    const next = value == null ? '' : String(value)
    setLocal(next)
    initialRef.current = next
  }, [value, editing])

  // Focus on entering edit mode. select-all for text-like inputs so retyping
  // is one keystroke; selects open on focus naturally.
  useEffect(() => {
    if (!editing) return
    const el = inputRef.current
    if (!el) return
    el.focus()
    if ((variant === 'text' || variant === 'number' || variant === 'date' || variant === 'time') && 'select' in el) {
      try { (el as HTMLInputElement).select() } catch { /* date/time may reject .select() — fine */ }
    }
  }, [editing, variant])

  const enterEdit = () => {
    if (isLocked) return
    setErrorMessage(null)
    initialRef.current = local
    setEditing(true)
  }

  const exitEdit = () => {
    setEditing(false)
    setErrorMessage(null)
  }

  const revertAndExit = () => {
    setLocal(initialRef.current)
    exitEdit()
  }

  const runValidate = (rawNext: any): string | null => {
    if (required) {
      const empty = rawNext == null || (typeof rawNext === 'string' && rawNext.trim() === '')
      if (empty) return "Can't be empty"
    }
    if (validate) return validate(rawNext)
    return null
  }

  const commit = async (nextValue: any) => {
    const err = runValidate(nextValue)
    if (err) {
      setErrorMessage(err)
      toast.error(err)
      return // stay in edit mode with the user's input still visible
    }
    setErrorMessage(null)
    setSaving(true)
    try {
      await onSave(nextValue)
      setSaving(false)
      exitEdit()
    } catch (e: any) {
      setSaving(false)
      const msg = e?.message || 'Failed to save'
      toast.error(msg)
      revertAndExit()
    }
  }

  const commitTextLike = () => {
    if (local === initialRef.current) { exitEdit(); return }
    if (variant === 'number') {
      const parsed = local === '' ? null : parseFloat(local)
      commit(parsed)
    } else {
      commit(local)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { revertAndExit(); return }
    if (e.key === 'Enter') {
      if (variant === 'textarea' && !(e.metaKey || e.ctrlKey) && !e.shiftKey) {
        // textarea: plain Enter inserts newline; commit on blur. ⌘/Ctrl+Enter commits explicitly.
        return
      }
      e.preventDefault()
      ;(e.target as HTMLElement).blur() // triggers commit via onBlur
    }
  }

  // ─── Read mode ────────────────────────────────────────────────────────────
  if (!editing) {
    const readDisplay = displayValue ?? (value == null || value === '' ? '' : String(value))
    const showPlaceholder = !readDisplay
    return (
      <div
        onClick={enterEdit}
        style={{
          padding: '5px 8px', borderRadius: 7,
          cursor: isLocked ? 'default' : 'pointer',
          background: 'transparent', border: '1px solid transparent',
          transition: 'background 120ms ease, border-color 120ms ease',
          minHeight: 28, display: 'flex', alignItems: 'center',
        }}
        onMouseEnter={e => { if (!isLocked) e.currentTarget.style.background = 'var(--hover-bg)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
      >
        <span style={{
          fontSize: 13,
          color: showPlaceholder ? 'var(--ink-tertiary)' : 'var(--ink-primary)',
          fontStyle: showPlaceholder ? 'italic' : 'normal',
          whiteSpace: variant === 'textarea' ? 'pre-wrap' : 'normal',
          lineHeight: 1.5,
          flex: 1,
          ...valueStyle,
        }}>
          {showPlaceholder ? (isLocked ? '—' : placeholder) : readDisplay}
        </span>
      </div>
    )
  }

  // ─── Edit mode ────────────────────────────────────────────────────────────
  const baseInputStyle: React.CSSProperties = {
    width: '100%', padding: '5px 8px',
    background: 'var(--field-bg)',
    border: `1px solid ${errorMessage ? 'var(--danger-border-strong)' : 'var(--focus-ring)'}`,
    borderRadius: 7, fontSize: 13, color: 'var(--ink-primary)', outline: 'none',
    boxSizing: 'border-box',
    ...valueStyle,
  }

  const renderInput = () => {
    switch (variant) {
      case 'textarea':
        return (
          <textarea
            ref={el => { inputRef.current = el }}
            value={local}
            onChange={e => setLocal(e.target.value)}
            onBlur={commitTextLike}
            onKeyDown={handleKeyDown}
            rows={3}
            style={{ ...baseInputStyle, resize: 'vertical', lineHeight: 1.5, minHeight: 64 }}
            disabled={saving}
          />
        )
      case 'select': {
        const opts = options ?? []
        const currentSelectValue = value == null ? '' : String(value)
        return (
          <select
            ref={el => { inputRef.current = el }}
            value={currentSelectValue}
            onChange={e => {
              const picked = opts.find(o => o.value === e.target.value) ?? null
              // Commit immediately — selects don't need blur-to-save.
              commit(picked)
            }}
            onBlur={() => { if (!saving) exitEdit() }}
            onKeyDown={(e) => { if (e.key === 'Escape') revertAndExit() }}
            style={{ ...baseInputStyle, cursor: 'pointer' }}
            disabled={saving}
          >
            {!required && <option value="">{placeholder || '—'}</option>}
            {opts.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )
      }
      case 'number':
        return (
          <input
            ref={el => { inputRef.current = el as any }}
            type="number"
            value={local}
            onChange={e => setLocal(e.target.value)}
            onBlur={commitTextLike}
            onKeyDown={handleKeyDown}
            style={baseInputStyle}
            disabled={saving}
          />
        )
      case 'date':
        return (
          <input
            ref={el => { inputRef.current = el as any }}
            type="date"
            value={local}
            onChange={e => setLocal(e.target.value)}
            onBlur={commitTextLike}
            onKeyDown={handleKeyDown}
            style={{ ...baseInputStyle, colorScheme: 'dark' }}
            disabled={saving}
          />
        )
      case 'time':
        return (
          <input
            ref={el => { inputRef.current = el as any }}
            type="time"
            value={local}
            onChange={e => setLocal(e.target.value)}
            onBlur={commitTextLike}
            onKeyDown={handleKeyDown}
            style={{ ...baseInputStyle, colorScheme: 'dark' }}
            disabled={saving}
          />
        )
      case 'text':
      default:
        return (
          <input
            ref={el => { inputRef.current = el as any }}
            type="text"
            value={local}
            onChange={e => setLocal(e.target.value)}
            onBlur={commitTextLike}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={baseInputStyle}
            disabled={saving}
          />
        )
    }
  }

  return (
    <div>
      {renderInput()}
      {errorMessage && (
        <p style={{ fontSize: 11, color: 'var(--danger)', margin: '4px 0 0 4px' }}>
          {errorMessage}
        </p>
      )}
    </div>
  )
}
